<!-- TEMPLATE: Drawer.vue -->
<!-- Navigation drawer shell — renders header, nav sections, and footer; handles lab-group toggle logic. -->

<script setup lang="ts">
//
// Imports
//
import DrawerHeader from "../organisms/DrawerHeader.vue";
import DrawerSection from "../organisms/DrawerSection.vue";
import DrawerItem from "../molecules/DrawerItem.vue";

//
// Props & Emits
//
defineProps<{
  appLogo?: string;
  appName?: string;
  userName?: string;
  userEmail?: string;
}>();

//
// Composables
//
const { drawer, drawerRailMode, operationalItems, administrativeItems, aboutItems, openedGroups } = useDrawer();

// Localization
const { t } = useI18n();

//
// State
//

/**
 * Tracks whether the lab group activator was explicitly clicked by the user.
 * Set on mousedown — fires before Vuetify's @update:opened handler.
 */
const labToggleClicked = ref(false);

//
// Methods
//

/**
 * Intercepts Vuetify's opened-groups updates.
 * When Laboratory is removed without the user clicking the group header
 * (e.g. Vuetify auto-close on route change), re-adds it to keep it open.
 * Always syncs with Vuetify's internal state to avoid double-click bugs.
 */
function handleOpenedChange(val: string[]) {
  const hadLab = openedGroups.value.includes("laboratory");
  const hasLab = val.includes("laboratory");

  if (hadLab && !hasLab && !labToggleClicked.value) {
    // Vuetify auto-close (e.g. route change) — keep it open
    openedGroups.value = [...val, "laboratory"];
    return;
  }

  labToggleClicked.value = false;
  openedGroups.value = val;
}
</script>

<template>
  <v-navigation-drawer v-model="drawer" app :rail="drawerRailMode">
    <!-- Header Section -->
    <DrawerHeader :app-name="appName" :user-name="userName" :user-email="userEmail" />

    <v-divider />

    <!-- Main Navigation Sections -->
    <DrawerSection
      :title="drawerRailMode ? '' : t('components.common.navigation-drawer.drawer_sections.operational_section.title')"
      :items="operationalItems"
    />

    <!-- Administrative: Laboratory collapsible group -->
    <v-list v-if="!drawerRailMode" :opened="openedGroups" density="compact" nav @update:opened="handleOpenedChange">
      <v-list-subheader class="text-uppercase text-caption text-medium-emphasis">
        {{ t("components.common.navigation-drawer.drawer_sections.administrative_section.title") }}
      </v-list-subheader>
      <v-list-group value="laboratory">
        <template #activator="{ props }">
          <v-list-item
            v-bind="props"
            prepend-icon="mdi-hospital-building"
            :title="t('components.common.navigation-drawer.laboratory')"
            rounded="lg"
            data-testid="nav-group-activator"
            @mousedown="labToggleClicked = true"
          />
        </template>
        <DrawerItem v-for="item in administrativeItems" :key="item.title" v-bind="item" data-testid="nav-group-item" />
      </v-list-group>
    </v-list>

    <!-- Rail mode: administrative items flat (no group header) -->
    <DrawerSection v-else :items="administrativeItems" />

    <!-- Footer Section -->
    <template #append>
      <v-divider />

      <DrawerSection :items="aboutItems" />
    </template>
  </v-navigation-drawer>
</template>
